import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
type CarouselImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
};
export function ImageCarousel() {
  const images: CarouselImage[] = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bg3ugaeMpnRCFDRjvZYpjw/m3.png',
    alt: 'Colección de libros de recetas de Stella incluyendo Pavlova, Milhojas, Tarta Ópera y Cheesecake',
    width: 800,
    height: 480
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uQcfTPjcaQDQ8c91zDXqhq/m4.png',
    alt: 'El libro "El Arte de la Milhojas o Rogel de Stella" mostrando una perfecta milhojas con merengue',
    width: 800,
    height: 480
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qqbHuBNYLVpyt4xabNJWUi/m2.png',
    alt: 'Stella Pereira en su cocina profesional sosteniendo su libro de recetas',
    width: 800,
    height: 480
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rtis39MrwdFxyHyZ5urzpK/m1.png',
    alt: 'Stella Pereira decorando un pastel con frutas frescas en su cocina profesional',
    width: 800,
    height: 480
  }];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(new Array(images.length).fill(false));
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);
  // Preload all images on component mount
  useEffect(() => {
    const preloadImages = () => {
      images.forEach((image, index) => {
        const img = new Image();
        img.src = image.src;
        img.onload = () => {
          setImagesLoaded(prev => {
            const newState = [...prev];
            newState[index] = true;
            return newState;
          });
        };
      });
    };
    preloadImages();
  }, []);
  // Auto-rotate carousel every 3 seconds
  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = window.setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % images.length);
    }, 3000);
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, images.length]);
  const goToPrevious = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPaused(true);
    setCurrentIndex(prevIndex => prevIndex === 0 ? images.length - 1 : prevIndex - 1);
  };
  const goToNext = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPaused(true);
    setCurrentIndex(prevIndex => prevIndex === images.length - 1 ? 0 : prevIndex + 1);
  };
  const goToSlide = (slideIndex: number) => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPaused(true);
    setCurrentIndex(slideIndex);
  };
  const handleMouseEnter = () => {
    setIsPaused(true);
  };
  const handleMouseLeave = () => {
    setIsPaused(false);
  };
  return <div className="relative w-full max-w-4xl mx-auto px-4 mb-12" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-207">
      {/* Loading indicator - only show if current image isn't loaded */}
      {!imagesLoaded[currentIndex] && <div className="absolute inset-0 flex items-center justify-center bg-amber-50 bg-opacity-50 z-20" data-id="element-208">
          <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" data-id="element-209"></div>
        </div>}
      {/* Image container with fixed aspect ratio */}
      <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-amber-50" data-id="element-210">
        {images.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-500 ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-211">
            <img src={image.src} alt={image.alt} className="w-full h-full object-cover" loading={index === 0 ? 'eager' : 'lazy'} width={image.width} height={image.height} decoding="async" data-id="element-212" />
          </div>)}
      </div>
      {/* Navigation Arrows */}
      <button onClick={goToPrevious} className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-amber-800 p-4 rounded-full shadow-md z-30 touch-manipulation" aria-label="Imagen anterior" data-id="element-213">
        <ChevronLeftIcon size={24} data-id="element-214" />
      </button>
      <button onClick={goToNext} className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-amber-800 p-4 rounded-full shadow-md z-30 touch-manipulation" aria-label="Imagen siguiente" data-id="element-215">
        <ChevronRightIcon size={24} data-id="element-216" />
      </button>
      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-4 z-30" data-id="element-217">
        {images.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-5 h-5 rounded-full ${currentIndex === index ? 'bg-amber-600' : 'bg-amber-300'}`} aria-label={`Ir a la imagen ${index + 1}`} data-id="element-218" />)}
      </div>
    </div>;
}